import { useId } from "react";

export const keyViewWidth = 160;
export const keyViewHeight = 60;
export const keyTipX = 2;

const heartPath =
  "M130 34.6c-2.7-1.8-4.8-3.6-4.8-5.7 0-1.5 1.1-2.6 2.5-2.6 1 0 1.8.5 2.3 1.3.5-.8 1.3-1.3 2.3-1.3 1.4 0 2.5 1.1 2.5 2.6 0 2.1-2.1 3.9-4.8 5.7z";

const shaftPath = "M2 30 L11 25.6 L97 25.6 L97 34.4 L11 34.4 Z";
const bitPath = "M14 34 L14 42.5 L19 42.5 L19 38.5 L24 38.5 L24 44.5 L30.5 44.5 L30.5 39.5 L35.5 39.5 L35.5 42.5 L42 42.5 L42 34 Z";

export default function LetterKey({ length }) {
  const rawId = useId().replace(/:/g, "");
  const ids = {
    shaft: `${rawId}-shaft`,
    bow: `${rawId}-bow`,
    medallion: `${rawId}-medallion`,
    collar: `${rawId}-collar`,
    specular: `${rawId}-specular`,
    blur: `${rawId}-blur`,
  };
  const height = (length * keyViewHeight) / keyViewWidth;
  const tipOffset = (length * keyTipX) / keyViewWidth;

  return (
    <svg
      viewBox={`0 0 ${keyViewWidth} ${keyViewHeight}`}
      width={length}
      height={height}
      style={{ position: "absolute", left: -tipOffset, top: -height / 2, overflow: "visible", display: "block" }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={ids.shaft} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fbe9b0" />
          <stop offset="0.2" stopColor="#e3bf66" />
          <stop offset="0.52" stopColor="#a87a2c" />
          <stop offset="0.8" stopColor="#74511a" />
          <stop offset="1" stopColor="#c39645" />
        </linearGradient>
        <linearGradient id={ids.collar} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff0c4" />
          <stop offset="0.3" stopColor="#d9b25c" />
          <stop offset="0.7" stopColor="#8a6322" />
          <stop offset="1" stopColor="#b98c3e" />
        </linearGradient>
        <radialGradient id={ids.bow} cx="0.36" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#fff2c6" />
          <stop offset="0.22" stopColor="#e7c672" />
          <stop offset="0.58" stopColor="#b5873a" />
          <stop offset="0.88" stopColor="#7a551c" />
          <stop offset="1" stopColor="#9f7630" />
        </radialGradient>
        <radialGradient id={ids.medallion} cx="0.62" cy="0.7" r="0.85">
          <stop offset="0" stopColor="#e9c878" />
          <stop offset="0.55" stopColor="#c09245" />
          <stop offset="1" stopColor="#8d6526" />
        </radialGradient>
        <radialGradient id={ids.specular} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <filter id={ids.blur} x="-20%" y="-40%" width="140%" height="180%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
      </defs>

      <g transform="translate(2.5 3.5)" fill="#5a2f22" opacity="0.2" filter={`url(#${ids.blur})`}>
        <path d={shaftPath} />
        <path d={bitPath} />
        <rect x="93" y="22.5" width="6" height="15" rx="1.6" />
        <rect x="100" y="20.5" width="6" height="19" rx="1.6" />
        <circle cx="130" cy="30" r="27" />
      </g>

      <path d={bitPath} fill={`url(#${ids.shaft})`} stroke="#5e4214" strokeOpacity="0.45" strokeWidth="0.6" />
      <path d={shaftPath} fill={`url(#${ids.shaft})`} stroke="#5e4214" strokeOpacity="0.45" strokeWidth="0.6" />
      <path d="M3.4 29.4 L11.4 26.6 L96 26.6" fill="none" stroke="#fff7d6" strokeOpacity="0.75" strokeWidth="0.9" strokeLinecap="round" />
      <path d="M44 31.2 L92 31.2" fill="none" stroke="#5e4214" strokeOpacity="0.28" strokeWidth="0.7" />

      <rect x="93" y="22.5" width="6" height="15" rx="1.6" fill={`url(#${ids.collar})`} stroke="#5e4214" strokeOpacity="0.4" strokeWidth="0.5" />
      <rect x="100" y="20.5" width="6" height="19" rx="1.6" fill={`url(#${ids.collar})`} stroke="#5e4214" strokeOpacity="0.4" strokeWidth="0.5" />

      <circle cx="130" cy="30" r="27" fill={`url(#${ids.bow})`} stroke="#5e4214" strokeOpacity="0.5" strokeWidth="0.7" />
      <circle cx="130" cy="30" r="23.2" fill="none" stroke="#fff0c0" strokeOpacity="0.5" strokeWidth="0.7" />
      <circle cx="130" cy="30" r="22.3" fill="none" stroke="#6a4818" strokeOpacity="0.55" strokeWidth="1.1" />
      <circle cx="130" cy="30" r="21.2" fill={`url(#${ids.medallion})`} />

      <g fontFamily="'Cormorant Garamond', 'Times New Roman', serif" fontWeight="500" fontSize="15" textAnchor="middle">
        <text x="119.6" y="35.6" fill="#fdeab6" fillOpacity="0.6">M</text>
        <text x="141" y="35.6" fill="#fdeab6" fillOpacity="0.6">S</text>
        <path d={heartPath} transform="translate(0.55 0.6)" fill="#fdeab6" fillOpacity="0.6" />
        <text x="119" y="35" fill="#5f4113" fillOpacity="0.9">M</text>
        <text x="140.4" y="35" fill="#5f4113" fillOpacity="0.9">S</text>
        <path d={heartPath} fill="#8f3b3b" fillOpacity="0.85" />
      </g>

      <ellipse cx="120" cy="15.5" rx="10" ry="4.2" transform="rotate(-28 120 15.5)" fill={`url(#${ids.specular})`} opacity="0.7" />
      <ellipse cx="40" cy="27.4" rx="16" ry="1.2" fill={`url(#${ids.specular})`} opacity="0.6" />
    </svg>
  );
}
